<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import * as Sentry from '@sentry/vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useADialog, useAToast } from '@Atoms';
import { API } from '@Providers';
import { useSessionStore } from '@Stores';
import { sortContactFields, useForm } from '@FormModule';
import { getSanitizedPayloadPeople } from '@PeopleModule';
import useShoppingCartStore from '../../../../store';
import useFields from './useFields';
import { AddPeopleToCartService, CompleteOutcomeService } from '../../../../services';
import { CART_STEPS } from '../../../../enums';
import { useCartSteps } from '../../../../composables';

const emits = defineEmits<{
  onStepUpdate: [CART_STEPS];
}>();

const { shelterProfile } = storeToRefs(useSessionStore());
const {
  anonymousDonations,
  cartData,
  completeOutcomeRoute,
  isDonation,
  isEquipmentRental,
  isProduct,
  people,
  peopleForm,
  stepState,
  outcomeType,
  type,
} = storeToRefs(useShoppingCartStore());
const { outcomeCompletedCartReset } = useShoppingCartStore();

const { activeDialog, activeModal, dialogVisibility, SYSTEM_MODALS } = useADialog();
const { notify, TOAST_TYPES } = useAToast();
const { fieldsValidation } = useForm();
const {
  contactData,
  currentSchemaModels,
  fieldsSchema,
  formError,
  formId,
  formName,
  formType,
  loadingContactData,
  personId,
  rawFields,
  setForm,
} = useFields();
const { currentStep, nextStep, nextStepButton, previousStep, OUTCOME_GUARDIAN_TYPES } = useCartSteps();
const { push } = useRouter();

const TOAST_ERROR = {
  message: 'errors.generics.unknownIssue.message',
  title: 'errors.generics.unknownIssue.title',
  type: TOAST_TYPES.ERROR,
};

const loading = ref(false);

const isGuardianOutcome = computed(() => OUTCOME_GUARDIAN_TYPES.includes(outcomeType.value));
const requirePeopleFormInRetail = computed(() => shelterProfile.value?.require_people_form_in_retail);

const showSkipButton = computed(
  () =>
    !isGuardianOutcome.value &&
    ((isProduct.value && !requirePeopleFormInRetail.value) || (isDonation.value && anonymousDonations.value))
);

const rentalLines = computed<Array<Record<string, unknown>>>(() => {
  const raw = (cartData.value as any)?.equipment_lines;
  return Array.isArray(raw) ? raw : [];
});

watch(loadingContactData, () => initPersonId());

onMounted(() => (currentStep.value = CART_STEPS.ADD_PEOPLE) && initPeopleForm());

// HELPERS
function initPeopleForm() {
  if (!peopleForm.value) return fetchPeopleForm();

  const { _id, form_type, form_name, fields } = peopleForm.value;
  formId.value = _id.$oid;
  formType.value = form_type;
  formName.value = form_name;

  rawFields.value = [...sortContactFields(fields)];

  setForm();
}

function initPersonId() {
  personId.value = people.value?.person_id;
}

// SERVICES
async function fetchPeopleForm() {
  const endpoint = '/api/shelter/forms/get-forms/peopleForms/peopleFields';

  return API({ endpoint }).then((result: any) => {
    peopleForm.value = result[0];
    initPeopleForm();
  });
}

function onNextStep() {
  if (loading.value) return;

  const hasErrors = fieldsValidation(fieldsSchema.value, currentSchemaModels.value);
  if (hasErrors) return (formError.value = true);

  formError.value = false;
  loading.value = true;

  function getSpecificFields() {
    const already_registered = !!personId.value || people.value?.already_registered;
    const already_adopted = people.value?.already_adopted || contactData.value?.groups.includes('adopter');
    const email_index = people.value?.matching_email_index || contactData.value?.matching_email_index || null;
    const phone_index = people.value?.matching_phone_index || contactData.value?.matching_phone_index || null;

    // TODO - AF-440 Add phone type to phone field on add person form
    const phone_type = people.value?.phone_type || contactData.value?.phone_type || 'mobile';

    const specificFields: Record<string, any> = {
      formId: formId.value,
      outcome_type: type.value,
      person_id: personId.value ? personId.value : people.value?.person_id,

      already_adopted,
      already_registered,

      email_index,
      phone_index,
      phone_type,
    };

    if (contactData.value?.staff_confirmed_use_of_do_not_adopt_person) {
      specificFields.staff_confirmed_use_of_do_not_adopt_person = true;
    }

    return specificFields;
  }

  const initialFields = getSanitizedPayloadPeople(currentSchemaModels.value);
  const specificFields = getSpecificFields();

  const { address1, address2, city, state, country, zip } = initialFields;

  const PAYLOAD = {
    ...initialFields,
    ...specificFields,
    address: {
      address1,
      address2,
      city,
      country,
      state,
      zip,
    },
  };

  // NOTE: Removing updated_at. Not used in the backend.
  delete (PAYLOAD as Partial<typeof PAYLOAD>).updated_at;
  AddPeopleToCartService(PAYLOAD)
    .then((people: any) => {
      cartData.value.people = people;
      (stepState.value as Record<string, any>)[currentStep.value] = PAYLOAD;

      if (!nextStep.value.step) return onCompleteOutcome();

      emits('onStepUpdate', nextStep.value.step!.name);
      loading.value = false;
    })
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (loading.value = false));
}

function onSkipAndComplete() {
  if (loading.value) return;

  loading.value = true;
  onCompleteOutcome();
}

function onCancelEquipmentRentalCheckout() {
  activeDialog.value = SYSTEM_MODALS.CART_EMPTY_DIALOG;
  dialogVisibility.value = true;
}

function onCompleteOutcome() {
  // NOTE :: TS compliance - completeOutcomeRoute should always exist when completing an outcome
  if (!completeOutcomeRoute.value) {
    Sentry.captureException(new Error('completeOutcomeRoute is null during onCompleteOutcome'), {
      tags: { component: 'MCartStepAdoptionInfo', type: 'ts-compliance' },
    });
  }

  activeModal.value = SYSTEM_MODALS.COMPLETE_OUTCOME_LOADING_MESSAGE;
  dialogVisibility.value = true;

  CompleteOutcomeService(cartData.value)
    .then(() => push(completeOutcomeRoute.value!).then(() => outcomeCompletedCartReset()))
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (loading.value = false));
}
</script>

<template>
  <div
    v-if="isEquipmentRental && rentalLines.length"
    class="af-cart-grid justify-center"
  >
    <div class="w-full">
      <div class="flex w-full flex-col gap-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div
          class="surface-border border-round surface-ground justify-content-center align-items-center flex w-full max-w-[700px] border-2 border-dashed p-4 font-medium"
        >
          <FormTemplate
            class="w-full"
            :formError
            :schema="fieldsSchema"
          />
        </div>

        <AFormError :formError />
      </div>
    </div>

    <aside class="w-full rounded-lg border border-gray-200 bg-white p-6 shadow-sm lg:sticky lg:top-4 lg:self-start">
      <header class="mb-4 flex items-center justify-between gap-2">
        <h3 class="text-lg font-semibold text-gray-900">
          {{ $t('equipmentRental.checkout.summaryHeading') }}
        </h3>

        <Button
          outlined
          class="af-button !text-xs"
          icon="pi pi-trash"
          severity="danger"
          :label="$t('cart.label.cancelCheckout')"
          @click="onCancelEquipmentRentalCheckout"
        />
      </header>

      <ul class="mb-4 flex flex-col gap-2 text-sm">
        <li
          v-for="(line, index) in rentalLines"
          :key="String((line as any).line_id ?? index)"
          class="rounded-md border border-gray-100 bg-slate-50 px-3 py-2"
        >
          <p class="font-medium text-gray-900">
            {{ (line as any).equipment_name || '—' }}
          </p>

          <p
            v-if="(line as any).equipment_id"
            class="text-xs text-gray-500"
          >
            {{ $t('equipmentRental.inventory.columns.equipment_label') }}: {{ (line as any).equipment_label || (line as any).equipment_id }}
          </p>
          <p
            v-if="(line as any).equipment_id"
            class="text-xs text-gray-500"
          >
            {{ $t('equipmentRental.inventory.columns.serial_number') }}: {{ (line as any).serial_number || '—' }}
          </p>
        </li>
      </ul>

      <Divider class="my-3" />

      <div class="flex flex-col gap-2">
        <Button
          class="af-button af-button-success w-full"
          iconPos="right"
          :label="$t(nextStepButton.label)"
          @click="onNextStep"
        >
          <template #icon>
            <FaIcon
              v-if="nextStepButton.icon"
              class="mr-2"
              :icon="$icons[nextStepButton.icon]"
            />
          </template>
        </Button>

        <Button
          v-if="previousStep"
          outlined
          class="af-button af-button-success-outlined w-full"
          icon="pi pi-arrow-left"
          :label="$t('common.back')"
          @click="emits('onStepUpdate', previousStep)"
        />
      </div>
    </aside>
  </div>

  <div
    v-else
    class="flex max-w-[700px] flex-col gap-4"
  >
    <FormTemplate
      :formError
      :schema="fieldsSchema"
    />

    <AFormError :formError />

    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex flex-wrap items-center gap-2">
        <Button
          v-if="previousStep"
          outlined
          class="af-button af-button-success-outlined w-fit"
          icon="pi pi-arrow-left"
          :label="$t('common.back')"
          @click="emits('onStepUpdate', previousStep)"
        />

        <Button
          v-if="isEquipmentRental"
          outlined
          class="af-button !min-h-[42px] w-fit !px-5 !text-sm !font-semibold md:!text-base"
          icon="pi pi-trash"
          severity="danger"
          :label="$t('equipmentRental.checkout.cancelCheckout')"
          @click="onCancelEquipmentRentalCheckout"
        />
      </div>

      <div class="ml-auto flex justify-end">
        <div class="ml-auto flex items-center gap-2">
          <Button
            v-if="showSkipButton"
            outlined
            class="af-button af-button-success-outlined ml-auto w-fit"
            iconPos="right"
            :label="$t('components.forms.button.skip')"
            @click="emits('onStepUpdate', nextStep.step!.name)"
          />

          <Button
            v-else-if="isGuardianOutcome"
            outlined
            class="af-button af-button-success-outlined ml-auto w-fit"
            iconPos="right"
            :label="$t('components.forms.button.skipAndComplete')"
            :loading
            @click="onSkipAndComplete"
          />

          <Button
            class="af-button af-button-success"
            iconPos="right"
            :label="$t(nextStepButton.label)"
            :loading
            @click="onNextStep"
          >
            <template #icon>
              <FaIcon
                v-if="nextStepButton.icon"
                class="mr-2"
                :icon="$icons[nextStepButton.icon]"
              />
            </template>
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
